import moment from "moment-timezone";
// Resolves after `ms`, or at once if the signal aborts first; the caller reads the
// signal to tell the two apart.
export function wait(ms: number, signal?: AbortSignal) {
  return new Promise<void>(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, ms);

    function abort() {
      clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener("abort", abort, { once: true });
  });
}

// Use for lazy singleton setup like SDK clients, where concurrent callers
// should share one initialization attempt without blocking future retries.
export function memoizeAsyncFactory<T>(factory: () => Promise<T>) {
  let promise: Promise<T> | undefined;

  return function get() {
    promise ??= factory().catch((error: unknown) => {
      promise = undefined;
      throw error;
    });

    return promise;
  };
}

type PollWithBackoffOptions<T> = {
  fn: () => Promise<T>;
  timeout: number; // ms
  minDelay: number; // ms
  backoffFactor?: number;
  shouldRetry?: (error: unknown) => boolean;
  // Ends the loop before the next attempt, by throwing its reason.
  signal?: AbortSignal;
};

const DEFAULT_BACKOFF_BASE = 2;

export async function pollWithBackoff<T>(options: PollWithBackoffOptions<T>): Promise<T> {
  const { fn, timeout, minDelay, backoffFactor, shouldRetry, signal } = {
    backoffFactor: DEFAULT_BACKOFF_BASE,
    shouldRetry: () => true,
    ...options,
  };

  const startTime = moment.utc().valueOf();
  let attempt = 0;
  let elapsedMs = 0;

  while (elapsedMs < timeout) {
    signal?.throwIfAborted();
    try {
      return await fn();
    } catch (error) {
      if (!shouldRetry(error)) {
        throw error;
      }
    }

    const remainingMs = timeout - elapsedMs;
    if (remainingMs > 0) {
      const delayMs = Math.min(minDelay * backoffFactor ** attempt, remainingMs);
      await wait(delayMs, signal);
    }

    attempt += 1;
    elapsedMs = moment.utc().valueOf() - startTime;
  }

  throw new PollingTimeoutError();
}

export class PollingTimeoutError extends Error {}
